import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
    Moon,
    Sun,
    Coffee,
    BatteryCharging,
    Wind,
    Brain,
    BookOpen,
    Star,
    Check,
    ChevronDown,
    ArrowRight,
    Pill,
    ShieldCheck,
    Lock,
    Download,
    Mail,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.7, ease: "easeOut" },
};

function formatPrice(amount, currency) {
    if (!amount || !currency) return "$17";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency,
            maximumFractionDigits: currency === "NGN" ? 0 : 2,
        }).format(amount);
    } catch {
        return `${amount} ${currency}`;
    }
}

function StarRow({ size = 16 }) {
    return (
        <div className="flex items-center gap-1" data-testid="star-row">
            {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={size} className="fill-clay text-clay" />
            ))}
        </div>
    );
}

function BuyButton({ id, priceLabel, className = "" }) {
    const [showEmail, setShowEmail] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const label = `Get The Guide — ${priceLabel}`;

    const handleStart = () => {
        setShowEmail(true);
        setError("");
    };

    const handleContinue = async () => {
        const trimmed = email.trim();
        if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            setError("Please enter a valid email address.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const res = await axios.post(`${API}/checkout/session`, {
                product_id: "sleep_reset",
                origin_url: window.location.origin,
                email: trimmed,
            });
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                setError("Checkout failed. Please try again.");
                setLoading(false);
            }
        } catch (e) {
            console.error(e);
            const msg =
                e?.response?.data?.detail ||
                "Could not start checkout. Please try again in a moment.";
            setError(msg);
            setLoading(false);
        }
    };

    if (!showEmail) {
        return (
            <div className="flex flex-col items-start gap-2">
                <button
                    data-testid={id}
                    onClick={handleStart}
                    className={`btn-primary ${className}`}
                >
                    {label}
                    <ArrowRight size={18} />
                </button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col gap-3 w-full max-w-md ${className}`}>
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-5 py-4 rounded-full bg-ink-800 border border-cream-soft/20 text-cream placeholder-cream-muted/60 focus:outline-none focus:border-clay transition-colors"
                    data-testid={`${id}-email-input`}
                    onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                />
                <button
                    onClick={handleContinue}
                    disabled={loading}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    data-testid={`${id}-continue`}
                >
                    {loading ? "Opening…" : "Continue"}
                    {!loading && <ArrowRight size={18} />}
                </button>
            </div>
            {error && (
                <span data-testid={`${id}-error`} className="text-sm text-red-300/90">
                    {error}
                </span>
            )}
            <p className="text-xs text-cream-muted/80">
                We'll email your receipt and a backup download link. No spam, ever.
            </p>
        </div>
    );
}

function Hero({ priceLabel }) {
    return (
        <section className="relative overflow-hidden grain min-h-[92vh] flex items-center moonlight">
            {/* Mobile-only: subtle background echo */}
            <div
                className="absolute inset-0 z-0 opacity-[0.22] md:hidden"
                style={{
                    backgroundImage: 'url("/hero-african-woman-stretching.jpg")',
                    backgroundSize: "cover",
                    backgroundPosition: "center 20%",
                }}
            />
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-ink-900/40 via-ink-900/85 to-ink-900 md:hidden" />

            <div className="container-prose relative z-10 py-24 md:py-32" data-testid="hero">
                <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
                    {/* Text column */}
                    <div className="md:col-span-7">
                        <motion.p {...fadeUp} className="label-eyebrow mb-6">
                            Restful Press · Digital Guide
                        </motion.p>
                        <motion.h1
                            {...fadeUp}
                            transition={{ duration: 0.8, delay: 0.05 }}
                            className="heading-display text-5xl md:text-6xl lg:text-7xl"
                            data-testid="hero-headline"
                        >
                            Tired of lying awake
                            <br />
                            <span className="italic text-clay">at 3 AM?</span>
                        </motion.h1>
                        <motion.p
                            {...fadeUp}
                            transition={{ duration: 0.8, delay: 0.15 }}
                            className="mt-8 text-lg md:text-xl text-cream-muted font-light max-w-2xl leading-relaxed"
                        >
                            A simple, science-backed 7-day reset to fall asleep faster, sleep deeper, and wake up
                            rested. <span className="text-cream">No pills. No gadgets. No 5 AM cult.</span>
                        </motion.p>

                        <motion.div
                            {...fadeUp}
                            transition={{ duration: 0.8, delay: 0.25 }}
                            className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6"
                        >
                            <BuyButton id="hero-buy-button" priceLabel={priceLabel} />
                            <div className="flex items-center gap-3">
                                <StarRow />
                                <span className="text-sm text-cream-muted">
                                    Trusted by 10,000+ exhausted humans
                                </span>
                            </div>
                        </motion.div>

                        <motion.div
                            {...fadeUp}
                            transition={{ duration: 0.8, delay: 0.35 }}
                            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-cream-muted"
                        >
                            <span className="flex items-center gap-2">
                                <ShieldCheck size={16} className="text-sage" />
                                30-day refund guarantee
                            </span>
                            <span className="flex items-center gap-2">
                                <Download size={16} className="text-sage" />
                                Instant PDF delivery
                            </span>
                            <span className="flex items-center gap-2">
                                <Lock size={16} className="text-sage" />
                                Secure Paystack checkout
                            </span>
                        </motion.div>
                    </div>

                    {/* Image column — desktop only */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.2 }}
                        className="hidden md:block md:col-span-5"
                        data-testid="hero-image"
                    >
                        <div className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden ring-1 ring-cream/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">
                            <img
                                src="/hero-african-woman-stretching.jpg"
                                alt="A young woman stretching peacefully upon waking, bathed in soft morning light."
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/40 via-transparent to-transparent" />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function WhoForSection() {
    const personas = [
        {
            icon: Brain,
            title: "The Anxious Thinker",
            text: "Your body is exhausted but your mind starts a podcast the second you close your eyes.",
        },
        {
            icon: Pill,
            title: "The Pill-Reluctant",
            text: "You've tried melatonin, magnesium, and herbal teas. They worked once, then stopped.",
        },
        {
            icon: Coffee,
            title: "The Tired-And-Wired",
            text: "Coffee no longer wakes you up. Wine no longer puts you down. Something has to give.",
        },
    ];
    return (
        <section className="container-prose py-24 md:py-32" data-testid="who-for-section">
            <motion.p {...fadeUp} className="label-eyebrow mb-4">
                Who this is for
            </motion.p>
            <motion.h2
                {...fadeUp}
                transition={{ duration: 0.7, delay: 0.05 }}
                className="heading-display text-4xl md:text-5xl max-w-3xl mb-16"
            >
                If any of these sound familiar,
                <span className="italic text-clay"> this is for you.</span>
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {personas.map((p, i) => {
                    const Icon = p.icon;
                    return (
                        <motion.div
                            key={p.title}
                            {...fadeUp}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="card-base p-8 md:p-10"
                            data-testid={`persona-${i}`}
                        >
                            <Icon size={28} className="text-clay mb-6" />
                            <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
                            <p className="text-cream-muted leading-relaxed font-light">{p.text}</p>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}

function InsideSection() {
    const days = [
        { d: "Day 1", t: "Anchor your wake-up time", desc: "The single change that resets your whole rhythm." },
        { d: "Day 2", t: "10 minutes of morning light", desc: "Why this beats any sleep supplement on the shelf." },
        { d: "Day 3", t: "Set a caffeine cutoff", desc: "The 8-hour rule that makes 11 PM feel sleepy again." },
        { d: "Day 4", t: "The 60-minute wind-down", desc: "Sleep is a slope, not a switch. Build the on-ramp." },
        { d: "Day 5", t: "Cool, dark, and boring", desc: "Turn your bedroom into a sleep sanctuary in one evening." },
        { d: "Day 6", t: "Master the 4-7-8 breath", desc: "A free, instant kill switch for a racing mind." },
        { d: "Day 7", t: "Lock it in for life", desc: "How to keep the routine alive after travel, illness, and chaos." },
    ];
    return (
        <section className="relative py-24 md:py-32 bg-ink-800/40" data-testid="inside-section">
            <div className="container-prose">
                <motion.p {...fadeUp} className="label-eyebrow mb-4">
                    What's inside
                </motion.p>
                <motion.h2
                    {...fadeUp}
                    transition={{ delay: 0.05 }}
                    className="heading-display text-4xl md:text-5xl max-w-3xl mb-4"
                >
                    Seven days. <span className="italic text-clay">One habit at a time.</span>
                </motion.h2>
                <motion.p {...fadeUp} transition={{ delay: 0.1 }} className="text-cream-muted text-lg max-w-2xl mb-16">
                    Each day adds one small change while keeping the previous ones. By Sunday you'll have a
                    complete sleep system you can run on autopilot.
                </motion.p>

                <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-start">
                    <motion.div
                        {...fadeUp}
                        className="md:col-span-5 sticky top-8"
                    >
                        <div className="relative" data-testid="at-a-glance-preview">
                            <div className="absolute -inset-6 bg-clay/15 blur-3xl rounded-full" />
                            <div className="absolute -inset-1 bg-gradient-to-br from-clay/30 to-transparent rounded-2xl blur-md" />
                            <img
                                src="/at-a-glance-preview.png"
                                alt="The 7 Days at a Glance — page from the actual guide"
                                className="relative rounded-2xl shadow-[0_20px_80px_rgba(0,0,0,0.4)] ring-1 ring-cream-soft/10 w-full"
                                data-testid="book-mockup"
                            />
                            <div className="absolute -top-3 -left-3 bg-cream text-ink-900 px-4 py-1.5 rounded-full font-medium text-xs flex items-center gap-1.5 shadow-lg">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-clay-deep" />
                                Page 3 · real preview
                            </div>
                            <div className="absolute -bottom-4 -right-4 bg-clay text-ink-900 px-5 py-3 rounded-full font-medium text-sm flex items-center gap-2 shadow-xl">
                                <BookOpen size={16} /> 30 pages · PDF
                            </div>
                        </div>
                        <p className="mt-8 text-sm text-cream-muted/90 leading-relaxed font-light max-w-sm">
                            <span className="text-cream">The whole 7-day system at a glance.</span>{" "}
                            One of the actual pages from the guide — so you know exactly what
                            you&apos;re getting.
                        </p>
                    </motion.div>

                    <div className="md:col-span-7 space-y-4">
                        {days.map((d, i) => (
                            <motion.div
                                key={d.d}
                                {...fadeUp}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="card-base p-6 md:p-7 flex items-start gap-5"
                                data-testid={`day-${i + 1}-card`}
                            >
                                <span className="font-serif text-3xl text-clay/80 leading-none w-16 shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.2em] text-cream-soft/70 mb-1">
                                        {d.d}
                                    </p>
                                    <h3 className="font-serif text-2xl mb-1.5">{d.t}</h3>
                                    <p className="text-cream-muted text-sm leading-relaxed font-light">
                                        {d.desc}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function BenefitsSection() {
    const items = [
        {
            icon: Moon,
            title: "Fall asleep in under 15 minutes",
            text: "Not because you're forcing it — because your body is finally ready.",
            big: true,
        },
        { icon: BatteryCharging, title: "Wake up before your alarm", text: "Energy that doesn't need a refill." },
        { icon: Wind, title: "Calm a racing mind in 90 seconds", text: "One breath pattern, anywhere." },
        { icon: Sun, title: "End the 3 PM slump", text: "Stop relying on a second coffee to function." },
        { icon: Pill, title: "No pills, no gadgets", text: "The whole system costs less than your morning latte." },
    ];
    return (
        <section className="container-prose py-24 md:py-32" data-testid="benefits-section">
            <motion.p {...fadeUp} className="label-eyebrow mb-4">
                The outcome
            </motion.p>
            <motion.h2
                {...fadeUp}
                transition={{ delay: 0.05 }}
                className="heading-display text-4xl md:text-5xl max-w-3xl mb-16"
            >
                What changes <span className="italic text-clay">by next Sunday.</span>
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                <motion.div
                    {...fadeUp}
                    className="md:col-span-7 card-base p-10 md:p-12 relative overflow-hidden min-h-[320px] flex flex-col justify-end"
                    data-testid="benefit-big"
                    style={{
                        backgroundImage:
                            'linear-gradient(to top, rgba(11,16,33,0.95), rgba(11,16,33,0.4)), url("https://images.unsplash.com/photo-1700190828205-d6ad6c06b682?crop=entropy&cs=srgb&fm=jpg&q=85")',
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                >
                    <Moon size={28} className="text-clay mb-4" />
                    <h3 className="font-serif text-3xl md:text-4xl mb-3 max-w-md">
                        Fall asleep in under 15 minutes.
                    </h3>
                    <p className="text-cream-muted max-w-md font-light">
                        Not because you're forcing it — because your body is finally ready.
                    </p>
                </motion.div>

                <div className="md:col-span-5 grid grid-cols-1 gap-6 md:gap-8">
                    {items.slice(1, 3).map((b, i) => {
                        const Icon = b.icon;
                        return (
                            <motion.div
                                key={b.title}
                                {...fadeUp}
                                transition={{ delay: i * 0.08 }}
                                className="card-base p-8"
                                data-testid={`benefit-${i + 1}`}
                            >
                                <Icon size={24} className="text-clay mb-4" />
                                <h3 className="font-serif text-xl mb-2">{b.title}</h3>
                                <p className="text-cream-muted text-sm leading-relaxed font-light">
                                    {b.text}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                {items.slice(3).map((b, i) => {
                    const Icon = b.icon;
                    return (
                        <motion.div
                            key={b.title}
                            {...fadeUp}
                            transition={{ delay: i * 0.08 }}
                            className="md:col-span-6 card-base p-8"
                            data-testid={`benefit-${i + 3}`}
                        >
                            <Icon size={24} className="text-clay mb-4" />
                            <h3 className="font-serif text-xl mb-2">{b.title}</h3>
                            <p className="text-cream-muted text-sm leading-relaxed font-light">{b.text}</p>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}

function TestimonialsSection() {
    const items = [
        {
            name: "Adaeze O.",
            role: "Architect, Lagos",
            quote:
                "I have spent four years and more naira than I want to admit on supplements. The thing that actually worked was a small PDF telling me to fix my wake time and step outside. Annoying. And true.",
            avatar: "/avatars/adaeze.png",
        },
        {
            name: "Kwame A.",
            role: "Software engineer, Accra",
            quote:
                "I read this during a 3 AM scroll expecting another generic listicle. By night three I was falling asleep before my wife. I have not opened the melatonin bottle in six weeks.",
            avatar: "/avatars/kwame.png",
        },
        {
            name: "Amani W.",
            role: "Postgraduate student, Nairobi",
            quote:
                "The 4-7-8 breath alone was worth more than I paid. I used to lie awake replaying the day for an hour. Now I am out before I finish counting.",
            avatar: "/avatars/amani.png",
        },
    ];
    return (
        <section className="container-prose py-24 md:py-32" data-testid="testimonials-section">
            <motion.p {...fadeUp} className="label-eyebrow mb-4">
                What readers say
            </motion.p>
            <motion.h2
                {...fadeUp}
                transition={{ delay: 0.05 }}
                className="heading-display text-4xl md:text-5xl max-w-3xl mb-16"
            >
                Real people. <span className="italic text-clay">Real sleep.</span>
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {items.map((t, i) => (
                    <motion.div
                        key={t.name}
                        {...fadeUp}
                        transition={{ delay: i * 0.1 }}
                        className="card-glass p-8 flex flex-col"
                        data-testid={`testimonial-${i}`}
                    >
                        <StarRow />
                        <p className="font-serif text-lg leading-relaxed mt-5 mb-6 text-cream">
                            &ldquo;{t.quote}&rdquo;
                        </p>
                        <div className="flex items-center gap-3 mt-auto">
                            <img
                                src={t.avatar}
                                alt={t.name}
                                className="w-12 h-12 rounded-full object-cover ring-1 ring-cream-soft/20"
                            />
                            <div>
                                <p className="font-medium text-cream">{t.name}</p>
                                <p className="text-xs text-cream-muted">{t.role}</p>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    );
}

function FAQSection() {
    const faqs = [
        {
            q: "Will this work if I have insomnia?",
            a: "For most stress- and lifestyle-driven sleep issues, yes — usually within a week. If you've been struggling for 3+ months or suspect sleep apnea (loud snoring, gasping awake), please pair this with a doctor visit.",
        },
        {
            q: "Do I need to give up coffee or alcohol?",
            a: "No. You'll learn to time them so they don't sabotage you. Most readers keep their morning coffee and an occasional glass of wine.",
        },
        {
            q: "Is this just generic 'sleep hygiene' tips?",
            a: "No. The order matters. Most lists give you 20 things and overwhelm you. This gives you one new habit per day, in the order that compounds — which is the difference between reading tips and actually sleeping.",
        },
        {
            q: "How long is the guide?",
            a: "30 pages. Designed to be skimmable in 30 minutes and actionable for life. Includes a printable tracker and a one-page quick reference card.",
        },
        {
            q: "What if it doesn't work?",
            a: "Email us within 30 days and we'll refund you in full. No forms, no questions. Sleep is too important to gatekeep.",
        },
        {
            q: "Will I get the file instantly?",
            a: "Yes. The moment your payment confirms you'll be redirected to a download page. The PDF works on phones, tablets, Kindle, and any computer.",
        },
    ];
    const [open, setOpen] = useState(0);
    return (
        <section className="py-24 md:py-32 bg-ink-800/40" data-testid="faq-section">
            <div className="max-w-3xl mx-auto px-6">
                <motion.p {...fadeUp} className="label-eyebrow mb-4 text-center">
                    Questions
                </motion.p>
                <motion.h2
                    {...fadeUp}
                    transition={{ delay: 0.05 }}
                    className="heading-display text-4xl md:text-5xl text-center mb-16"
                >
                    Anything <span className="italic text-clay">on your mind.</span>
                </motion.h2>
                <div className="space-y-1">
                    {faqs.map((f, i) => (
                        <div
                            key={i}
                            className="border-b border-cream-soft/10 py-2"
                            data-testid={`faq-item-${i}`}
                        >
                            <button
                                onClick={() => setOpen(open === i ? -1 : i)}
                                className="w-full flex items-center justify-between gap-6 py-5 text-left"
                                data-testid={`faq-trigger-${i}`}
                            >
                                <span className="font-serif text-lg md:text-xl text-cream">{f.q}</span>
                                <ChevronDown
                                    size={20}
                                    className={`shrink-0 text-clay transition-transform duration-300 ${
                                        open === i ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {open === i && (
                                <motion.p
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ duration: 0.3 }}
                                    className="text-cream-muted leading-relaxed pb-5 font-light"
                                >
                                    {f.a}
                                </motion.p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function LeadMagnetSection() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [error, setError] = useState("");
    const [downloadUrl, setDownloadUrl] = useState("");
    const [alreadySubscribed, setAlreadySubscribed] = useState(false);

    const isValid = /.+@.+\..+/.test(email.trim());

    const submit = async (e) => {
        e.preventDefault();
        if (!isValid) {
            setError("Please enter a valid email address.");
            return;
        }
        setStatus("loading");
        setError("");
        try {
            const res = await axios.post(`${API}/lead/subscribe`, {
                email: email.trim(),
                source: "landing",
            });
            setDownloadUrl(res.data.day1_url);
            setAlreadySubscribed(res.data.already_subscribed);
            setStatus("success");
        } catch (err) {
            setError(
                err?.response?.data?.detail?.[0]?.msg ||
                    err?.response?.data?.detail ||
                    "Something went wrong. Try again in a moment."
            );
            setStatus("error");
        }
    };

    return (
        <section
            className="relative py-24 md:py-32 bg-ink-800/40 border-y border-cream-soft/10"
            data-testid="lead-magnet-section"
        >
            <div className="max-w-2xl mx-auto px-6 text-center">
                <motion.p {...fadeUp} className="label-eyebrow mb-4">
                    Not ready yet?
                </motion.p>
                <motion.h2
                    {...fadeUp}
                    transition={{ delay: 0.05 }}
                    className="heading-display text-4xl md:text-5xl mb-6"
                >
                    Try <span className="italic text-clay">Day 1 free.</span>
                </motion.h2>
                <motion.p
                    {...fadeUp}
                    transition={{ delay: 0.1 }}
                    className="text-lg text-cream-muted mb-10 font-light"
                >
                    Get the first day of the guide as a PDF — the exact same page from the paid edition.
                    Anchor your wake time tomorrow. Zero-cost, zero-risk.
                </motion.p>

                {status === "success" ? (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="bg-ink-900/60 border border-clay/40 rounded-xl p-6 md:p-8"
                        data-testid="lead-magnet-success"
                    >
                        <div className="flex items-center justify-center gap-2 mb-3 text-clay">
                            <Check size={20} />
                            <span className="font-serif text-lg">
                                {alreadySubscribed ? "You're already on the list." : "You're in."}
                            </span>
                        </div>
                        <p className="text-cream-muted mb-6">
                            {alreadySubscribed
                                ? "Here's your Day 1 PDF again — no email resent."
                                : "Check your inbox for a welcome email with the free Day 1 PDF."}
                        </p>
                        <a
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-clay hover:bg-clay/90 text-ink-900 font-medium px-6 py-3 rounded-lg transition"
                            data-testid="lead-magnet-download-btn"
                        >
                            <Download size={18} />
                            Download Day 1 (PDF)
                        </a>
                    </motion.div>
                ) : (
                    <motion.form
                        {...fadeUp}
                        transition={{ delay: 0.15 }}
                        onSubmit={submit}
                        className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                        data-testid="lead-magnet-form"
                    >
                        <div className="relative flex-1">
                            <Mail
                                size={16}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-cream-muted pointer-events-none"
                            />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError("");
                                }}
                                placeholder="your@email.com"
                                required
                                data-testid="lead-magnet-email-input"
                                className="w-full bg-ink-900/60 border border-cream-soft/20 rounded-lg pl-11 pr-4 py-3 text-cream placeholder:text-cream-muted focus:outline-none focus:border-clay/60 transition"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={status === "loading" || !isValid}
                            data-testid="lead-magnet-submit-btn"
                            className="bg-clay hover:bg-clay/90 disabled:opacity-50 disabled:cursor-not-allowed text-ink-900 font-medium px-6 py-3 rounded-lg transition inline-flex items-center justify-center gap-2 whitespace-nowrap"
                        >
                            {status === "loading" ? "Sending…" : (
                                <>
                                    Send me Day 1 <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </motion.form>
                )}
                {error && status !== "success" && (
                    <p className="text-red-300 text-sm mt-4" data-testid="lead-magnet-error">
                        {error}
                    </p>
                )}
                <p className="text-xs text-cream-muted mt-6 max-w-sm mx-auto">
                    We'll send you the PDF plus a short 4-email series. Unsubscribe with one click.
                </p>
            </div>
        </section>
    );
}

function FinalCTA({ priceLabel }) {
    return (
        <section className="relative dawn-gradient grain py-28 md:py-40 overflow-hidden" data-testid="final-cta">
            <div className="container-prose relative z-10 text-center">
                <motion.div
                    {...fadeUp}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream-soft/10 border border-cream-soft/20 text-sm text-cream mb-8"
                >
                    <ShieldCheck size={16} className="text-sage" />
                    30-day money-back guarantee
                </motion.div>
                <motion.h2
                    {...fadeUp}
                    transition={{ delay: 0.05 }}
                    className="heading-display text-5xl md:text-7xl max-w-3xl mx-auto mb-6"
                >
                    Tomorrow morning,
                    <br />
                    <span className="italic text-clay">it could already be different.</span>
                </motion.h2>
                <motion.p
                    {...fadeUp}
                    transition={{ delay: 0.1 }}
                    className="text-lg text-cream-muted max-w-xl mx-auto mb-12 font-light"
                >
                    One PDF. Seven days. The cost of a takeaway dinner. Worst case, you get your money back.
                    Best case, you sleep like you used to.
                </motion.p>
                <motion.div
                    {...fadeUp}
                    transition={{ delay: 0.15 }}
                    className="flex flex-col items-center gap-5"
                >
                    <BuyButton id="final-buy-button" />
                    <div className="flex items-center gap-3">
                        <StarRow />
                        <span className="text-sm text-cream-muted">10,000+ readers · 4.9 average</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function StickyMobileCTA({ priceLabel }) {
    const [show, setShow] = useState(false);
    useEffect(() => {
        const onScroll = () => setShow(window.scrollY > 600);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div
            className={`md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pt-3 bg-gradient-to-t from-ink-900 via-ink-900/95 to-transparent transition-transform duration-500 ${
                show ? "translate-y-0" : "translate-y-full"
            }`}
            data-testid="sticky-mobile-cta"
        >
            <BuyButton id="sticky-buy-button" priceLabel={priceLabel} className="w-full justify-center" />
        </div>
    );
}

function Footer() {
    return (
        <footer className="border-t border-cream-soft/10 py-12" data-testid="footer">
            <div className="container-prose flex flex-col md:flex-row items-center justify-between gap-6">
                <p className="font-serif text-xl">Restful Press</p>
                <div className="flex flex-col md:flex-row md:items-center gap-4 text-sm text-cream-muted text-center md:text-right">
                    <a href="/privacy" className="hover:text-clay transition" data-testid="footer-privacy-link">
                        Privacy
                    </a>
                    <a href="/refund" className="hover:text-clay transition" data-testid="footer-refund-link">
                        Refunds
                    </a>
                    <a href="mailto:hello@restfulpress.com" className="hover:text-clay transition" data-testid="footer-contact-link">
                        Contact
                    </a>
                    <span className="text-cream-muted/50 hidden md:inline">·</span>
                    <span className="text-cream-muted/70">
                        Made with care, for tired humans.
                    </span>
                </div>
            </div>
        </footer>
    );
}

export default function Landing() {
    const [priceLabel, setPriceLabel] = useState("$17");

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/product/sleep_reset`)
            .then((res) => {
                if (!cancelled && res.data?.amount && res.data?.currency) {
                    setPriceLabel(formatPrice(res.data.amount, res.data.currency));
                }
            })
            .catch(() => {
                /* keep default */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="bg-ink-900 text-cream min-h-screen">
            <Hero priceLabel={priceLabel} />
            <WhoForSection />
            <InsideSection />
            <BenefitsSection />
            <TestimonialsSection />
            <FAQSection />
            <LeadMagnetSection />
            <FinalCTA priceLabel={priceLabel} />
            <Footer />
            <StickyMobileCTA priceLabel={priceLabel} />
        </div>
    );
}
